import React, { useState } from 'react';
import { Star, ChevronDown, ChevronUp, Search, TrendingUp, Info } from 'lucide-react';
import { kornFerrySkills, getCategoryLabel, getCategoryColor } from '../../data/kornFerrySkills';
interface SkillLevel {
  skillId: number;
  level: number; // 0-100
  status: 'not-started' | 'in-development' | 'proficient' | 'expert';
}
interface KornFerrySkillsProps {
  selectedSkills?: number[];
  skillLevels?: SkillLevel[];
  trackedSkills?: number[]; // Compétences suivies avec l'employé (étoiles jaunes)
  onSkillsChange?: (skills: number[]) => void;
  onLevelsChange?: (levels: SkillLevel[]) => void;
  onTrackedSkillsChange?: (skills: number[]) => void;
  mode?: 'view' | 'select' | 'assess';
  showTracking?: boolean; // Afficher les étoiles pour marquer les compétences suivies
}
const getStatusLabel = (status: string) => {
  switch (status) {
    case 'not-started':
      return 'Non démarré';
    case 'in-development':
      return 'En développement';
    case 'proficient':
      return 'Compétent';
    case 'expert':
      return 'Expert';
    default:
      return status;
  }
};
const getStatusFromLevel = (level: number): SkillLevel['status'] => {
  if (level === 0) return 'not-started';
  if (level < 50) return 'in-development';
  if (level < 85) return 'proficient';
  return 'expert';
};
const getLevelColor = (level: number) => {
  if (level === 0) return 'bg-gray-300';
  if (level < 50) return 'bg-red-500';
  if (level < 70) return 'bg-orange-500';
  if (level < 85) return 'bg-yellow-500';
  return 'bg-green-500';
};
const getSliderColor = (level: number) => {
  if (level === 0) return '#d1d5db';
  if (level < 50) return '#ef4444';
  if (level < 70) return '#f97316';
  if (level < 85) return '#eab308';
  return '#22c55e';
};
const getStatusColor = (status: string) => {
  switch (status) {
    case 'not-started':
      return 'bg-gray-100 text-gray-800';
    case 'in-development':
      return 'bg-orange-100 text-orange-800';
    case 'proficient':
      return 'bg-blue-100 text-blue-800';
    case 'expert':
      return 'bg-green-100 text-green-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};
export function KornFerrySkills({
  selectedSkills = [],
  skillLevels = [],
  trackedSkills = [],
  onSkillsChange,
  onLevelsChange,
  onTrackedSkillsChange,
  mode = 'view',
  showTracking = false
}: KornFerrySkillsProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedCategories, setExpandedCategories] = useState<string[]>(['leadership', 'thought', 'results', 'self']);
  const [expandedDescriptions, setExpandedDescriptions] = useState<number[]>([]);
  const toggleCategory = (category: string) => {
    setExpandedCategories(prev => prev.includes(category) ? prev.filter(c => c !== category) : [...prev, category]);
  };
  const toggleDescription = (skillId: number) => {
    setExpandedDescriptions(prev => prev.includes(skillId) ? prev.filter(id => id !== skillId) : [...prev, skillId]);
  };
  const toggleSkill = (skillId: number) => {
    if (mode === 'view' || !onSkillsChange) return;
    const newSelected = selectedSkills.includes(skillId) ? selectedSkills.filter(id => id !== skillId) : [...selectedSkills, skillId];
    onSkillsChange(newSelected);
  };
  const toggleTrackedSkill = (skillId: number, event: React.MouseEvent) => {
    event.stopPropagation();
    if (!onTrackedSkillsChange) return;
    const newTracked = trackedSkills.includes(skillId) ? trackedSkills.filter(id => id !== skillId) : [...trackedSkills, skillId];
    onTrackedSkillsChange(newTracked);
  };
  const handleLevelChange = (skillId: number, newLevel: number) => {
    if (!onLevelsChange) return;
    const existingLevelIndex = skillLevels.findIndex(sl => sl.skillId === skillId);
    const newStatus = getStatusFromLevel(newLevel);
    let newLevels: SkillLevel[];
    if (existingLevelIndex >= 0) {
      newLevels = [...skillLevels];
      newLevels[existingLevelIndex] = {
        skillId,
        level: newLevel,
        status: newStatus
      };
    } else {
      newLevels = [...skillLevels, {
        skillId,
        level: newLevel,
        status: newStatus
      }];
    }
    onLevelsChange(newLevels);
  };
  const getSkillLevel = (skillId: number): SkillLevel => {
    return skillLevels.find(sl => sl.skillId === skillId) || {
      skillId,
      level: 0,
      status: 'not-started'
    };
  };
  const filteredSkills = kornFerrySkills.filter(skill => skill.titleFr.toLowerCase().includes(searchTerm.toLowerCase()) || skill.description.toLowerCase().includes(searchTerm.toLowerCase()));
  // Filter skills based on mode
  const displaySkills = mode === 'assess' ? filteredSkills.filter(skill => selectedSkills.includes(skill.id)) : filteredSkills;
  const groupedSkills = displaySkills.reduce((acc, skill) => {
    if (!acc[skill.category]) {
      acc[skill.category] = [];
    }
    acc[skill.category].push(skill);
    return acc;
  }, {} as Record<string, typeof kornFerrySkills>);
  return <div className="space-y-4" data-id="element-1426">
      {(mode === 'select' || mode === 'assess') && <div className="relative" data-id="element-1427">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" data-id="element-1428" />
          <input type="text" placeholder="Rechercher une compétence..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-1429" />
        </div>}

      {mode === 'select' && selectedSkills.length > 0 && <div className="bg-blue-50 border border-blue-200 rounded-lg p-3" data-id="element-1430">
          <p className="text-sm text-blue-900" data-id="element-1431">
            <strong data-id="element-1432">{selectedSkills.length}</strong> compétence(s)
            sélectionnée(s)
          </p>
        </div>}

      {showTracking && trackedSkills.length > 0 && <div className="bg-amber-50 border border-amber-200 rounded-lg p-3" data-id="element-1433">
          <p className="text-sm text-amber-900" data-id="element-1434">
            <Star className="h-4 w-4 inline mr-1 text-amber-600 fill-amber-600" data-id="element-1435" />
            <strong data-id="element-1436">{trackedSkills.length}</strong> compétence(s) suivie(s) -
            Cliquez sur l'étoile pour ajuster le niveau
          </p>
        </div>}

      {mode === 'assess' && <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-1437">
          <div className="flex items-start gap-3" data-id="element-1438">
            <TrendingUp className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" data-id="element-1439" />
            <div data-id="element-1440">
              <h3 className="font-semibold text-blue-900 mb-1" data-id="element-1441">
                Évaluation des compétences
              </h3>
              <p className="text-sm text-blue-700 leading-relaxed" data-id="element-1442">
                Glissez la poignée sur la barre pour ajuster le niveau de
                maturité de chaque compétence. La couleur indique votre
                progression : rouge (débutant), orange (en développement), jaune
                (compétent), vert (expert).
              </p>
            </div>
          </div>
        </div>}

      <div className="space-y-3" data-id="element-1443">
        {Object.entries(groupedSkills).map(([category, skills]) => {
        const isExpanded = expandedCategories.includes(category);
        const categoryColor = getCategoryColor(category);
        return <div key={category} className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-1444">
              <button onClick={() => toggleCategory(category)} className={`w-full flex items-center justify-between p-4 ${categoryColor} hover:opacity-80 transition-opacity`} data-id="element-1445">
                <div className="flex items-center gap-2" data-id="element-1446">
                  <span className="font-semibold" data-id="element-1447">
                    {getCategoryLabel(category)}
                  </span>
                  <span className="text-sm opacity-75" data-id="element-1448">({skills.length})</span>
                </div>
                {isExpanded ? <ChevronUp className="h-5 w-5" data-id="element-1449" /> : <ChevronDown className="h-5 w-5" data-id="element-1450" />}
              </button>

              {isExpanded && <div className="bg-white p-4 space-y-3" data-id="element-1451">
                  {skills.map(skill => {
              const isSelected = selectedSkills.includes(skill.id);
              const isTracked = trackedSkills.includes(skill.id);
              const skillLevel = getSkillLevel(skill.id);
              const levelColor = getLevelColor(skillLevel.level);
              const sliderColor = getSliderColor(skillLevel.level);
              const statusColor = getStatusColor(skillLevel.status);
              const isDescriptionExpanded = expandedDescriptions.includes(skill.id);
              return <div key={skill.id} onClick={() => mode === 'select' && toggleSkill(skill.id)} className={`p-4 rounded-lg border-2 transition-all ${mode === 'select' ? 'cursor-pointer' : ''} ${isSelected && mode === 'select' ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`} data-id="element-1452">
                        <div className="flex items-start justify-between gap-3 mb-2" data-id="element-1453">
                          <div className="flex items-center gap-2 flex-1" data-id="element-1454">
                            {/* ÉTOILE TOUJOURS VISIBLE - Jaune si suivie, grise sinon */}
                            {showTracking && <button onClick={e => toggleTrackedSkill(skill.id, e)} className="flex-shrink-0 hover:scale-110 transition-transform" title={isTracked ? 'Retirer du suivi' : 'Ajouter au suivi'} data-id="element-1455">
                                <Star className={`h-5 w-5 ${isTracked ? 'text-amber-500 fill-amber-500' : 'text-gray-300'}`} data-id="element-1456" />
                              </button>}
                            <h4 className="font-medium text-gray-900" data-id="element-1457">
                              {skill.titleFr}
                            </h4>
                          </div>

                          <div className="flex items-center gap-2" data-id="element-1458">
                            {mode === 'select' && <div className={`flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center ${isSelected ? 'bg-blue-600 border-blue-600' : 'border-gray-300'}`} data-id="element-1459">
                                {isSelected && <svg className="w-3 h-3 text-white" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor" data-id="element-1460">
                                    <path d="M5 13l4 4L19 7" data-id="element-1461"></path>
                                  </svg>}
                              </div>}

                            {skillLevel.level > 0 && <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColor}`} data-id="element-1462">
                                {getStatusLabel(skillLevel.status)}
                              </span>}

                            <button onClick={e => {
                      e.stopPropagation();
                      toggleDescription(skill.id);
                    }} className="flex-shrink-0 p-1 hover:bg-gray-100 rounded transition-colors" title="Voir la description" data-id="element-1463">
                              <Info className="h-4 w-4 text-gray-500" data-id="element-1464" />
                            </button>
                          </div>
                        </div>

                        {/* Description */}
                        {isDescriptionExpanded && <div className="mb-3 p-3 bg-gray-50 rounded-lg border border-gray-200" data-id="element-1465">
                            <p className="text-sm text-gray-700 leading-relaxed" data-id="element-1466">
                              {skill.description}
                            </p>
                          </div>}

                        {/* BARRE DE PROGRESSION APPARAÎT SEULEMENT SI L'ÉTOILE EST CLIQUÉE (isTracked) */}
                        {showTracking && isTracked && <div className="space-y-3 mt-4" data-id="element-1467">
                            <div className="flex justify-between items-center text-sm" data-id="element-1468">
                              <span className="text-gray-600 font-medium" data-id="element-1469">
                                Niveau de maturité
                              </span>
                              <span className="font-bold text-gray-900 text-lg" data-id="element-1470">
                                {skillLevel.level}%
                              </span>
                            </div>

                            <div className="space-y-2" data-id="element-1471">
                              <div className="relative py-2" data-id="element-1472">
                                <input type="range" min="0" max="100" step="5" value={skillLevel.level} onChange={e => handleLevelChange(skill.id, parseInt(e.target.value))} className="w-full" style={{
                        background: `linear-gradient(to right, ${sliderColor} 0%, ${sliderColor} ${skillLevel.level}%, #e5e7eb ${skillLevel.level}%, #e5e7eb 100%)`,
                        color: sliderColor
                      }} data-id="element-1473" />
                              </div>
                              <div className="flex justify-between text-xs text-gray-500 font-medium" data-id="element-1474">
                                <span data-id="element-1475">0% - Débutant</span>
                                <span data-id="element-1476">100% - Expert</span>
                              </div>
                            </div>
                          </div>}

                        {/* Mode assess - toujours afficher la barre */}
                        {mode === 'assess' && isSelected && <div className="space-y-3 mt-4" data-id="element-1477">
                            <div className="flex justify-between items-center text-sm" data-id="element-1478">
                              <span className="text-gray-600 font-medium" data-id="element-1479">
                                Niveau de maturité
                              </span>
                              <span className="font-bold text-gray-900 text-lg" data-id="element-1480">
                                {skillLevel.level}%
                              </span>
                            </div>

                            <div className="space-y-2" data-id="element-1481">
                              <div className="relative py-2" data-id="element-1482">
                                <input type="range" min="0" max="100" step="5" value={skillLevel.level} onChange={e => handleLevelChange(skill.id, parseInt(e.target.value))} className="w-full" style={{
                        background: `linear-gradient(to right, ${sliderColor} 0%, ${sliderColor} ${skillLevel.level}%, #e5e7eb ${skillLevel.level}%, #e5e7eb 100%)`,
                        color: sliderColor
                      }} data-id="element-1483" />
                              </div>
                              <div className="flex justify-between text-xs text-gray-500 font-medium" data-id="element-1484">
                                <span data-id="element-1485">0% - Débutant</span>
                                <span data-id="element-1486">100% - Expert</span>
                              </div>
                            </div>
                          </div>}

                        {/* Mode view - afficher seulement la barre de progression statique */}
                        {mode === 'view' && isSelected && !showTracking && skillLevel.level > 0 && <div className="space-y-2 mt-4" data-id="element-1487">
                              <div className="flex justify-between items-center text-sm mb-2" data-id="element-1488">
                                <span className="text-gray-600 font-medium" data-id="element-1489">
                                  Niveau de maturité
                                </span>
                                <span className="font-bold text-gray-900 text-lg" data-id="element-1490">
                                  {skillLevel.level}%
                                </span>
                              </div>
                              <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden" data-id="element-1491">
                                <div className={`h-3 rounded-full transition-all duration-300 ${levelColor}`} style={{
                      width: `${skillLevel.level}%`
                    }} data-id="element-1492"></div>
                              </div>
                            </div>}
                      </div>;
            })}
                </div>}
            </div>;
      })}
      </div>
    </div>;
}